/**
 * Bootstrap-once factory for `@nodii/grpc-auth`. Per
 * `06-library-integration § 4.3` + `01-communication-doctrine § 13.3`,
 * every service that exposes or calls gRPC calls this once at process
 * start; the resulting singleton drives `withAuthUnaryValue`,
 * `mfaRequiredInterceptor`, `createClientAuthInterceptor`, and the
 * 13-stack composer in `@nodii/grpc-interceptors`.
 *
 * REPLAY ENFORCEMENT IS A DEPRECATED OPT-IN, OFF BY DEFAULT — hub decision
 * D845 (locked 2026-09-18). Callers cache and reuse one S2S token for its
 * lifetime, so a receiver that rejects a repeated `jti` cannot be called by
 * them. `enforceJtiReplay` defaults to `false`, and a `replayStore` reaches the
 * verifier only when the flag is explicitly `true`.
 *
 * R1 (NON-NEGOTIABLE) still holds FOR THE OPT-IN: there is no in-memory
 * `ReplayStore` default. `enforceJtiReplay: true` without a `replayStore` is a
 * hard `ConfigError` at boot.
 *
 * Idempotency: a second call with the **same** config (deep-equal
 * shallow on the primitive fields + identity on the injected
 * resolver/store/manager) is a no-op. A second call with a different
 * config throws `ConfigError` — services do not "re-bootstrap"
 * in the middle of a process; that's a sign of a config bug.
 *
 * Reset for testing: `resetGrpcAuthConfigForTesting()` is exported but
 * intentionally NOT re-exported from the package root. Tests import it
 * via the `./configure` subpath.
 */
import type { S2STokenManager } from "./client/s2s-token-manager";
import type { UserPermissionResolver } from "./server/check-user-permission";
import { getRequiredScopesRegistry } from "./server/require-permission";
import type { ReplayStore } from "./types/replay-store";
import { processGlobalSlot } from "./global-state";
import {
  resetJwksResolverForTesting,
  setJwksResolver,
} from "./verify/s2s-verify";
import type { GetKeyFn } from "./verify/sigv4-s3-jwks";

/**
 * Thrown by `configureGrpcAuth` when the supplied options violate R1
 * or when a second call uses a different config than the first.
 *
 * Distinguished from `S2SVerifyError` because this is a configuration
 * error, not a verifier-runtime error — downstream consumers may want
 * to fail-fast at startup on this class specifically.
 */
export class ConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConfigError";
  }
}

export type ConfigureGrpcAuthOptions = {
  expectedIssuer: string;
  expectedAudience: string;
  jwksResolver: GetKeyFn;
  /**
   * Redis-backed (or equivalent) jti-replay store. Used ONLY when
   * `enforceJtiReplay: true` — and then it is REQUIRED (R1: no in-memory
   * default). Ignored, with a one-time warning, when the flag is off.
   */
  replayStore?: ReplayStore;
  /**
   * DEPRECATED opt-in, default `false` — hub decision D845 (2026-09-18).
   * Callers cache and reuse one S2S token for its lifetime, so a receiver that
   * rejects a repeated `jti` cannot be called by them. Enabling this is
   * incompatible with caching callers (`@nodii/grpc-auth` >= 0.17.0). When
   * `true`, `replayStore` is required and `configureGrpcAuth` throws without
   * it. Under this opt-in a token with no `jti` is rejected (`missing_jti`).
   */
  enforceJtiReplay?: boolean;
  /**
   * Optional S2S token-issuer manager (client-side). Carried through
   * the config so consumers can pull it off `getGrpcAuthConfig()`
   * inside `createClientAuthInterceptor`.
   */
  tokenManager?: S2STokenManager;
  /**
   * Per-method required-scope map. Merged with the
   * `@requirePermission`-populated registry at bootstrap time;
   * caller-supplied entries WIN on key collision (so consumers can
   * override a decorator-registered scope from the call site).
   */
  requiredScopesByMethod?: Record<string, string>;
  /**
   * Clock skew tolerance (seconds) passed through to `verifyS2SToken`.
   * Defaults to 10s.
   */
  maxClockSkewSeconds?: number;
  /**
   * Default-DENY for unlisted gRPC methods (request e95b3100, closes the
   * F1.5 default-ALLOW class — D429).
   *
   * When `true` (DEFAULT as of 0.15.0): a method ABSENT from
   * `requiredScopesByMethod` is REJECTED with `PERMISSION_DENIED` (the message
   * names the unlisted method). The verifier refuses to serve any RPC that has
   * not been explicitly bound to a required scope. Listed methods are
   * scope-checked exactly as before.
   *
   * When `false`: a method ABSENT from `requiredScopesByMethod` skips the scope
   * gate entirely; only token-validity is enforced.
   *
   * BREAKING CHANGE IN 0.15.0 — the default flipped from `false` to `true`.
   *
   * The old default was the canonical fail-open shape: authorization ran only
   * when a lookup happened to return something, so forgetting to register a
   * method did not fail — it silently removed the check. Any holder of ANY valid
   * S2S token could invoke the unregistered RPC, including a destructive one,
   * and nothing anywhere reported it. A missing registration is invisible in
   * exactly the way a missing authorization decision must never be.
   *
   * Setting this to `false` re-opens that hole knowingly. It is a legitimate
   * staged-rollout step (enumerate every served method, then remove the
   * override) and an illegitimate way to make a failing deploy go away.
   */
  denyUnlistedMethods?: boolean;
  /**
   * Globally-configured resolver for handler-side user RBAC
   * (`checkUserPermission`). Returns the user's EFFECTIVE permission
   * keys in the given tenant — the consumer resolves
   * `user -> roles -> permissions` against its own replicas. REQUIRED
   * for `checkUserPermission` to work; if absent, that call throws
   * `CheckUserPermissionNotConfigured` (R1: there is no default
   * resolver).
   */
  userPermissionResolver?: UserPermissionResolver;
};

/** Frozen, post-bootstrap config snapshot. */
export type GrpcAuthConfig = {
  expectedIssuer: string;
  expectedAudience: string;
  jwksResolver: GetKeyFn;
  replayStore?: ReplayStore;
  enforceJtiReplay: boolean;
  tokenManager?: S2STokenManager;
  requiredScopesByMethod: Record<string, string>;
  maxClockSkewSeconds: number;
  denyUnlistedMethods: boolean;
  userPermissionResolver?: UserPermissionResolver;
};

// Process-global slot (NOT a module-level `let`) so the tsup `splitting: false`
// subpath bundles share ONE config — e.g. `configureGrpcAuth({...})` on the root
// and `getGrpcAuthConfigOrNull()` read from `@nodii/grpc-auth/server`'s
// check-user-permission must see the same value (the twin of req 4f1facd4).
const configSlot = processGlobalSlot<GrpcAuthConfig>("config");

function shallowEqualConfig(a: GrpcAuthConfig, b: GrpcAuthConfig): boolean {
  if (a.expectedIssuer !== b.expectedIssuer) return false;
  if (a.expectedAudience !== b.expectedAudience) return false;
  if (a.jwksResolver !== b.jwksResolver) return false;
  if (a.replayStore !== b.replayStore) return false;
  if (a.enforceJtiReplay !== b.enforceJtiReplay) return false;
  if (a.tokenManager !== b.tokenManager) return false;
  if (a.maxClockSkewSeconds !== b.maxClockSkewSeconds) return false;
  if (a.denyUnlistedMethods !== b.denyUnlistedMethods) return false;
  if (a.userPermissionResolver !== b.userPermissionResolver) return false;
  const aKeys = Object.keys(a.requiredScopesByMethod);
  const bKeys = Object.keys(b.requiredScopesByMethod);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a.requiredScopesByMethod[k] !== b.requiredScopesByMethod[k]) {
      return false;
    }
  }
  return true;
}

/**
 * Wire the package. Call once at process start, before any
 * `withAuthUnaryValue` / `mfaRequiredInterceptor` /
 * `createClientAuthInterceptor` call.
 *
 * Throws `ConfigError` on missing required fields or on a
 * second call with a different config.
 */
const warned = new Set<string>();
/** One `console.warn` per process per key — config runs at boot, not per call. */
function warnOnce(key: string, message: string): void {
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(message);
}

export function configureGrpcAuth(opts: ConfigureGrpcAuthOptions): void {
  if (!opts.expectedIssuer) {
    throw new ConfigError("configureGrpcAuth: expectedIssuer is required");
  }
  if (!opts.expectedAudience) {
    throw new ConfigError("configureGrpcAuth: expectedAudience is required");
  }
  if (!opts.jwksResolver) {
    throw new ConfigError("configureGrpcAuth: jwksResolver is required");
  }

  // Hub decision D845: replay enforcement is OFF unless explicitly enabled.
  const enforceJtiReplay = opts.enforceJtiReplay ?? false;
  if (enforceJtiReplay && !opts.replayStore) {
    throw new ConfigError(
      "configureGrpcAuth: replayStore is required when enforceJtiReplay=true (R1: no in-memory default; pass a Redis-backed ReplayStore)",
    );
  }
  if (enforceJtiReplay) {
    warnOnce(
      "replay-enabled",
      "[@nodii/grpc-auth] enforceJtiReplay=true is DEPRECATED and incompatible with caching callers (hub decision D845): a caller on >= 0.17.0 reuses one S2S token for its lifetime, and this service will reject every call after the first.",
    );
  } else if (opts.replayStore) {
    warnOnce(
      "store-ignored",
      "[@nodii/grpc-auth] a replayStore was passed but enforceJtiReplay is off (the default since hub decision D845) — the store is IGNORED. Remove it, or set enforceJtiReplay: true to opt in.",
    );
  }

  // Merge the @requirePermission registry with caller-supplied overrides.
  // Caller-supplied entries WIN on key collision so consumers can override
  // a decorator-registered scope from the call site.
  const merged: Record<string, string> = {
    ...getRequiredScopesRegistry(),
    ...(opts.requiredScopesByMethod ?? {}),
  };

  const next: GrpcAuthConfig = {
    expectedIssuer: opts.expectedIssuer,
    expectedAudience: opts.expectedAudience,
    jwksResolver: opts.jwksResolver,
    replayStore: opts.replayStore,
    enforceJtiReplay,
    tokenManager: opts.tokenManager,
    requiredScopesByMethod: merged,
    maxClockSkewSeconds: opts.maxClockSkewSeconds ?? 10,
    denyUnlistedMethods: opts.denyUnlistedMethods ?? true,
    userPermissionResolver: opts.userPermissionResolver,
  };

  const existing = configSlot.get();
  if (existing) {
    if (shallowEqualConfig(existing, next)) {
      return; // idempotent
    }
    throw new ConfigError(
      "configureGrpcAuth: already configured with a different config — re-bootstrap is a config bug (call resetGrpcAuthConfigForTesting() in tests)",
    );
  }

  setJwksResolver(opts.jwksResolver);
  configSlot.set(next);
}

/**
 * Read the post-bootstrap config. Throws if `configureGrpcAuth` has
 * not run yet — call sites can either let this propagate (which
 * crashes the request with a clear message) or catch and surface a
 * 503-equivalent. Per R1 there's no silent default.
 */
export function getGrpcAuthConfig(): GrpcAuthConfig {
  const cfg = configSlot.get();
  if (!cfg) {
    throw new ConfigError(
      "getGrpcAuthConfig: configureGrpcAuth(...) has not been called",
    );
  }
  return cfg;
}

/**
 * Non-throwing peek at the post-bootstrap config (D308 / request
 * f4d47a07). Returns the module singleton, or `null` when
 * `configureGrpcAuth` has not run yet — it does NOT throw.
 *
 * This exists so the pure-functional `checkUserPermission` helper can
 * fall back to a globally-configured `userPermissionResolver` WITHOUT
 * forcing the full JWT-verification config (`expectedIssuer` /
 * `expectedAudience` / `jwksResolver`) to be present. Per-service-auth
 * consumers that pass the resolver inline never need `configureGrpcAuth`
 * at all.
 */
export function getGrpcAuthConfigOrNull(): GrpcAuthConfig | null {
  return configSlot.get();
}

/**
 * Test-only reset hook. NOT re-exported from the package root; tests
 * import it via the `@nodii/grpc-auth/configure` subpath (or, in this
 * monorepo, directly from `./configure`).
 */
export function resetGrpcAuthConfigForTesting(): void {
  configSlot.set(null);
  // configureGrpcAuth wires BOTH the config and the resolver, so a clean reset
  // must clear both (the resolver is now process-global; otherwise it leaks).
  resetJwksResolverForTesting();
  // The once-per-process warnings count as config state: a test must be able
  // to observe them again after a reset.
  warned.clear();
}
